import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Smartphone, ShieldCheck, ShieldOff } from 'lucide-react';
import { auth } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { fmtDateTime } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ResetAuthenticatorDialog } from './ResetAuthenticatorDialog';
import { PageHeader } from '@/components/layout/PageHeader';

/**
 * F2 — /me/profile.
 *
 * Read-only view of the caller's own account with two self-service
 * actions: change password + reset authenticator. Editing name / email
 * is intentionally out of scope — those changes flow through the admin
 * console (audit trail and site-scope enforcement).
 */
export function ProfilePage() {
  const { user } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => auth.me(),
    staleTime: 60_000,
  });
  const me = meQuery.data;

  const roleLabel = user?.role ? humanRole(user.role) : '—';
  const siteName = me ? (me.site_names[me.site_id] ?? me.site_id) : '—';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Your profile"
        subtitle="Manage your password and authenticator. To change your name, email, or role, contact your Site Administrator."
      />
      <div className="mx-auto max-w-3xl space-y-6">

      {/* Identity card */}
      <section className="bg-white border border-border-soft rounded-md shadow-card p-5">
        <h2 className="text-[13px] font-bold text-navy uppercase tracking-wider mb-4">Identity</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
          <div>
            <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Full name</dt>
            <dd className="font-semibold text-text-1">{user?.fullName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Email</dt>
            <dd className="font-semibold text-text-1 break-all">{user?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Role</dt>
            <dd className="font-semibold text-text-1">{roleLabel}</dd>
          </div>
          <div>
            <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Active site</dt>
            <dd className="font-semibold text-text-1">{siteName}</dd>
          </div>
          {me?.recovery_codes && (
            <div>
              <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Recovery codes</dt>
              <dd className="font-semibold text-text-1">
                {me.recovery_codes.remaining} of {me.recovery_codes.enrolled_count} remaining
                {me.recovery_codes.low && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-strong">
                    Low
                  </span>
                )}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-text-3 text-[11px] uppercase tracking-wider mb-0.5">Session started</dt>
            <dd className="font-semibold text-text-1">{fmtDateTime(new Date().toISOString())}</dd>
          </div>
        </dl>
      </section>

      {/* Security card */}
      <section className="bg-white border border-border-soft rounded-md shadow-card p-5">
        <h2 className="text-[13px] font-bold text-navy uppercase tracking-wider mb-4">Security</h2>

        <div className="flex items-start justify-between border-b border-border-soft pb-4 mb-4">
          <div className="flex items-start gap-3">
            <KeyRound size={18} className="text-navy mt-0.5" />
            <div>
              <div className="text-[13px] font-semibold text-text-1">Password</div>
              <div className="text-[12px] text-text-3">
                Choose a new password. Must be at least 12 characters and can&apos;t match your last 5 passwords.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPwdOpen(true)}
            className="h-9 px-3 rounded-sm border border-navy text-navy text-[12px] font-bold hover:bg-navy hover:text-white transition-colors"
          >
            Change password
          </button>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Smartphone size={18} className="text-navy mt-0.5" />
            <div>
              <div className="text-[13px] font-semibold text-text-1 flex items-center gap-2">
                Authenticator app
                {me?.mfa_enrolled ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success-strong">
                    <ShieldCheck size={11} /> Enrolled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-strong">
                    <ShieldOff size={11} /> Not enrolled
                  </span>
                )}
              </div>
              <div className="text-[12px] text-text-3">
                Use this to move your authenticator to a new device. You&apos;ll re-enrol on your next sign-in.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMfaOpen(true)}
            disabled={!me?.mfa_enrolled}
            className="h-9 px-3 rounded-sm border border-navy text-navy text-[12px] font-bold hover:bg-navy hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset authenticator
          </button>
        </div>
      </section>

      <ChangePasswordDialog
        open={pwdOpen}
        onOpenChange={setPwdOpen}
        userEmail={user?.email ?? ''}
        userFullName={user?.fullName ?? ''}
      />
      <ResetAuthenticatorDialog open={mfaOpen} onOpenChange={setMfaOpen} />
      </div>
    </div>
  );
}
